import React, { useState, useEffect } from 'react';
import { Snackbar, Box, Typography, TextField, FormControl, InputLabel, Select, MenuItem, Button, Stack, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Avatar, LinearProgress, Tooltip, IconButton, Grid, Chip, Alert, TablePagination, Paper, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { CheckCircle, Search, PendingActions, Timeline, Group, Visibility, RestartAlt, Inbox, Close as CloseIcon, Edit, History } from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import GoalDetailsPage from './goalDetailsPage';
import StatsCard from '../../../../components/common/StatsCard';

const MOCK_GOALS = [
    {
        id: 1,
        name: "Bharath M",
        goalTitle: "Frontend Optimization",
        target: "Reduce LCP by 20%",
        progress: 75,
        deadline: "2026-05-15",
        status: "In Progress",
        department: "Engineering",
        position: "Senior Frontend Developer",
        overallRating: 4.2
    },
    {
        id: 2,
        name: "Surya Teja",
        goalTitle: "API Documentation",
        target: "Complete Swagger Docs",
        progress: 100,
        deadline: "2026-03-30",
        status: "Completed",
        department: "Backend",
        position: "SDE-II",
        overallRating: 4.8
    },
    {
        id: 3,
        name: "Jay M",
        goalTitle: "Client Onboarding",
        target: "Onboard 5 New Clients",
        progress: 20,
        deadline: "2026-06-01",
        status: "Pending",
        department: "Sales",
        position: "Manager",
        overallRating: 3.5
    },
    {
        id: 4,
        name: "Anil Kumar",
        goalTitle: "Security Audit",
        target: "Zero Critical Vulnerabilities",
        progress: 90,
        deadline: "2026-04-20",
        status: "Submitted For Review",
        department: "DevOps",
        position: "Security Lead",
        overallRating: 4.5
    },
    {
        id: 5,
        name: "Priya Sharma",
        goalTitle: "UI Component Library",
        target: "Migrate to MUI v6",
        progress: 60,
        deadline: "2026-05-30",
        status: "In Progress",
        department: "Engineering",
        position: "UI Developer",
        overallRating: 4.0
    }
];

function EmployeeGoalsPage() {
    const navigate = useNavigate();
    const [state, setState] = useState({
        searchQuery: '',
        filterStatus: 'All',
        selectedCardFilter: 'all',
        allGoals: [],
        totalCount: 0,
        stats: {},
        loading: false,
        tablePage: 0,
        rowsPerPage: 10,
        selectedGoal: '',
        isGoalDetailsOpen: false,
        isEditing: false,
        snackbar: { open: false, message: '', severity: 'success' }
    });

    const updateState = (updates) => setState(prev => ({ ...prev, ...updates }));

    useEffect(() => {
        updateState({
            allGoals: MOCK_GOALS,
            totalCount: MOCK_GOALS.length
        });
    }, []);

    const handleStatusChange = (val) => {
        updateState({ filterStatus: val, selectedCardFilter: val, tablePage: 0 });
    };

    const getStatusChip = (status) => {
        const config = { 'Submitted For Review': 'warning', 'Pending': 'error', 'In Progress': 'info', 'Completed': 'success' };
        return <Chip label={status} color={config[status] || 'default'} size="small" />;
    };

    const handleSave = (updatedGoal) => {
        // Here you would typically call an API
        const updatedList = state.allGoals.map(g => g.id === updatedGoal.id ? updatedGoal : g);
        updateState({
            allGoals: updatedList,
            isEditing: false,
            selectedGoal: updatedGoal,
            snackbar: { open: true, message: 'Goal updated successfully!', severity: 'success' }
        });
    };

    return (
        <Box>
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {[
                    { title: 'Total Goals', value: 40, icon: <Group />, color: 'linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)' },
                    { title: 'Pending', value: 5, icon: <PendingActions />, color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
                    { title: 'In Progress', value: 35, icon: <Timeline />, color: 'linear-gradient(135deg, #5ee7df 0%, #b490ca 100%)' },
                    { title: 'Submitted For Review', value: 12, icon: <CheckCircle />, color: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)' },
                ].map((card, idx) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={idx}>
                        <StatsCard
                            title={card.title}
                            value={card.value || 0}
                            icon={card.icon}
                            color={card.color}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
                <Grid size={{ xs: 12, sm: 4, md: 8 }}>
                    <TextField
                        fullWidth size="small"
                        placeholder="Search employees..."
                        value={state.searchQuery}
                        onChange={(e) => updateState({ searchQuery: e.target.value, tablePage: 0 })}
                        InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'gray' }} /> }}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status"
                            value={state.filterStatus}
                            onChange={(e) => handleStatusChange(e.target.value)}
                        >
                            {['All', 'Pending', 'Submitted For Review', 'In Progress', 'Completed'].map(s => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 4, md: 1 }}>
                    <Button
                        startIcon={<RestartAlt />}
                        fullWidth
                        onClick={() => updateState({ searchQuery: '', filterStatus: 'All', selectedCardFilter: 'all', tablePage: 0 })}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                    >
                        Reset
                    </Button>
                </Grid>
            </Grid>

            {/* Table Area */}
            <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', overflow: 'hidden', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                            <TableRow>
                                {['Employee', 'Goal Title', 'Target', 'Progress', 'Deadline', 'Status', 'Actions'].map((h) => (
                                    <TableCell
                                        key={h}
                                        align={h === 'Actions' ? 'center' : 'left'}
                                        sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}
                                    >
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                            {state.allGoals.length > 0 ? (
                                state.allGoals.map((emp) => (
                                    <TableRow key={emp.id} hover sx={{
                                        '&:hover': {
                                            bgcolor: '#F1F5F9 !important'
                                        }
                                    }}>
                                        <TableCell sx={{ color: '#1E293B' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Avatar sx={{ width: 32, height: 32, mr: 1.5, fontSize: '0.8rem', bgcolor: 'primary.main' }}>
                                                    {emp.name[0]?.toUpperCase()}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="600">{emp.name}</Typography>
                                                    <Typography variant="caption" color="textSecondary">{emp.department}</Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: '#1E293B' }}>
                                            <Typography variant="body2" fontWeight="500">{emp.goalTitle}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 150, color: '#1E293B' }}>
                                            <Typography variant="caption" color="textSecondary">{emp.target}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ color: '#1E293B' }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Box sx={{ width: '100%', mr: 1 }}>
                                                    <LinearProgress variant="determinate" value={emp.progress} sx={{ height: 6, borderRadius: 3 }} />
                                                </Box>
                                                <Typography variant="caption" fontWeight="bold">{emp.progress}%</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ color: '#1E293B' }}>
                                            <Typography variant="body2">{new Date(emp.deadline).toLocaleDateString('en-GB')}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ color: '#1E293B' }}>{getStatusChip(emp.status)}</TableCell>
                                        <TableCell sx={{ color: '#1E293B' }} align="center">
                                            <Tooltip title="View Details">
                                                <IconButton size="small" onClick={() => updateState({ selectedGoal: emp, isGoalDetailsOpen: true })}>
                                                    <Visibility fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="View past goals">
                                                <IconButton size="small" onClick={() => navigate('/manager/view-past-goals')}>
                                                    <History fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                        <Inbox sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                                        <Typography variant="body1" color="textSecondary">No records found.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </TableContainer>
            <TablePagination
                component="div"
                count={state.totalCount}
                page={state.tablePage}
                rowsPerPage={state.rowsPerPage}
                onPageChange={(_, p) => updateState({ tablePage: p })}
                onRowsPerPageChange={(e) => updateState({ rowsPerPage: parseInt(e.target.value, 10), tablePage: 0 })}
            />

            <Dialog
                open={state.isGoalDetailsOpen}
                onClose={() => updateState({ isGoalDetailsOpen: false })}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8f9fa' }}>
                    {/* Change Typography to Box and set component="span" */}
                    <Box component="span" sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                        Goal Details
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {!state.isEditing ? (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Edit sx={{ fontSize: 18 }} />}
                                onClick={() => updateState({ isEditing: true })}
                                sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 600, mr: 1 }}
                            >
                                Edit
                            </Button>
                        ) : (
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="text"
                                    color="inherit"
                                    size="small"
                                    onClick={() => updateState({ isEditing: false })}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    type="submit"
                                    form="goal-edit-form" // Link to the form in the child component
                                    sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 600 }}
                                >
                                    Save Changes
                                </Button>
                            </Stack>
                        )}
                        <IconButton onClick={() => updateState({ isGoalDetailsOpen: false })} sx={{ color: (theme) => theme.palette.grey[500] }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <GoalDetailsPage goalData={state.selectedGoal}
                        isEditing={state.isEditing}
                        onSave={handleSave} />
                </DialogContent>
            </Dialog>

            <Snackbar
                open={state.snackbar.open}
                autoHideDuration={3000}
                onClose={() => updateState({ snackbar: { ...state.snackbar, open: false } })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={state.snackbar.severity}>{state.snackbar.message}</Alert>
            </Snackbar>
        </Box >
    );
};

export default EmployeeGoalsPage;